import * as O from './options.js';
import { stateOptions } from './states.js';
import { CHANNEL, isCorrespondent } from '../../../utils/channelUtils.js';
import { getOrgConfig } from '../../../utils/organizationConfigUtil.js';
import {
    LoanType,
    calculateLoanType,
    isAccelerator,
    isActivator,
    isAlly,
    isAmbassador,
    isAmbassadorIncome,
    isAmbassadorSecondHomeIncome,
    isAcceleratorIncome,
    isBusinessPurpose,
    isItinAccelerator,
    isItinActivator,
    isSuperJumbo,
    productName,
    SUPER_JUMBO_THRESHOLD,
    productNamePrefix,
} from './loanTypes.js';

export const SLIDER_LIMITS = {
    fico: { min: 300, max: 850, default: 700 },
    ltv: { min: 1, max: 90, default: 75 },
    loanAmount: { min: 100_000, max: 5_000_000, default: 500_000 },
};

export const INITIAL_FORM = {
    channel: CHANNEL.WHOLESALE,
    citizenship: '0',
    incomeType: '21',
    occupancy: '0',
    purpose: '0',
    fico: SLIDER_LIMITS.fico.default,
    noFico: false,
    ltv: SLIDER_LIMITS.ltv.default,
    loanAmount: SLIDER_LIMITS.loanAmount.default,
    lockPeriod: '30',
    amortizationType: '2',
    bankStatementMonths: '12',
    closingEntity: '2',
    preAssetDepletion: '0.75-0.99',
    interestOnly: false,
    ach: false,
    escrowWaiver: false,
    cdfiEligible: false,
    shortTermRental: false,
    rentFree: false,
    assetDepletion: false,
    isTexas50A6: false,
    lenderFeeBuyout: false,
    propertyType: '0',
    units: '1',
    state: 'AZ',
    brokerCompensation: '1',
    compPlanRate: '1',
    dti: '20',
    ysp: '0.000',
    pppTerm: '0',
    housingEvent: '1',
    creditSeasoning: '5',
    lienPosition: '1',
};

const DSCR_INCOME_TYPES = ['20', '21', '700', '701', '702', '703'];
const OCCUPANCY_LOCKING_INCOME_TYPES = ['20', '700', '701', '702', '703', '800', '801', '802'];
const NO_PPP_STATES = ['AK', 'CO', 'MD', 'NM', 'WI', 'VT', 'MI', 'NJ', 'OH', 'PA'];

const pick = (list, allowed) => list.filter((o) => allowed.includes(o.value));

/** Income-type label matters: several rules test it for "DSCR" / "No Ratio". */
function incomeLabelFor(citizenship, incomeType) {
    if (citizenship === '4' && incomeType === '21') return 'No Ratio';
    return O.findLabel(O.INCOME_TYPE, incomeType);
}

/* ------------------------------------------------------------------ options */

function incomeTypeOptions(citizenship, correspondent) {
    if (citizenship === '4') return pick(O.INCOME_TYPE, O.INCOME_TYPES_ITIN);
    if (!correspondent) {
        const hidden = getOrgConfig().hiddenIncomeTypes ?? [];
        const allowed = O.INCOME_TYPES_STANDARD.filter((v) => !hidden.includes(v));
        return pick(O.INCOME_TYPE, allowed);
    }

    // Correspondent has no no-income product; foreign nationals gain Foreign Income.
    const allowed = [...O.INCOME_TYPES_CORRESPONDENT];
    if (citizenship === '3') allowed.push(O.INCOME_TYPE_FOREIGN);
    return pick(O.INCOME_TYPE, allowed);
}

/** What the income-type dropdown falls back to when the current value is gone. */
const defaultIncomeType = (citizenship, correspondent) => {
    if (citizenship === '4') return '0';
    if (!correspondent) return getOrgConfig().defaultIncomeType ?? '21';
    return '900';
};

function amortizationOptions(typeCode) {
    if (isSuperJumbo(typeCode)) return pick(O.AMORTIZATION_TYPE, ['2']);
    if (isAmbassador(typeCode)) return pick(O.AMORTIZATION_TYPE, ['2', '4', '5']);
    if (isAccelerator(typeCode)) return pick(O.AMORTIZATION_TYPE, ['2', '4', '5', '6']);
    if (isAlly(typeCode)) return pick(O.AMORTIZATION_TYPE, ['2', '5', '6']);
    if (isActivator(typeCode)) return pick(O.AMORTIZATION_TYPE, ['2', '5', '6']);
    if (isItinActivator(typeCode)) return pick(O.AMORTIZATION_TYPE, ['1', '2']);
    if (isItinAccelerator(typeCode)) return pick(O.AMORTIZATION_TYPE, ['2']);
    return O.AMORTIZATION_TYPE;
}

function propertyTypeOptions(typeCode) {
    // Condotel is withheld from the credit-qualifying products.
    const allowCondotel = !(isAlly(typeCode) || isActivator(typeCode));
    return O.PROPERTY_TYPE.filter((o) => o.value !== '4D' || allowCondotel);
}

/** Unit count is only a real choice for Multi-Unit; otherwise it is pinned to 1. */
function unitAvailability(typeCode, propertyType, incomeLabel) {
    if (propertyType !== 'MU') return { allowed: ['1'], fallback: '1' };
    const allowed = ['2', '3', '4'];
    if (isAccelerator(typeCode) && incomeLabel.includes('DSCR')) allowed.push('5', '6', '7', '8');
    return { allowed, fallback: '2' };
}

function purposeOptions(typeCode) {
    // Debt Consolidation is an Activator / ITIN Activator feature.
    const allowDebtConsolidation = [
        LoanType.ACTIVATOR,
        LoanType.ITIN_ACTIVATOR_WHOLESALE,
        LoanType.ITIN_ACTIVATOR_CORRND,
    ].includes(typeCode);
    return O.PURPOSE.filter((o) => o.value !== '3' || allowDebtConsolidation);
}

function dtiRule(typeCode, occupancy, incomeType, incomeLabel) {
    if (isSuperJumbo(typeCode)) {
        return { visible: true, disabled: false, options: pick(O.DTI, ['38', '39']), fallback: '38' };
    }
    if (isActivator(typeCode) || isAcceleratorIncome(typeCode, incomeType, incomeLabel)) {
        return {
            visible: true,
            disabled: false,
            options: pick(O.DTI, ['20', '44', '46', '51', '56']),
            fallback: '20',
        };
    }
    if (isAmbassadorIncome(typeCode, occupancy, incomeType)) {
        return {
            visible: true,
            disabled: false,
            options: pick(O.DTI, ['20', '44', '46', '55']),
            fallback: '20',
        };
    }
    if (isAlly(typeCode)) {
        // Shown, but there is nothing to choose — Ally is a no-ratio product.
        return { visible: true, disabled: true, options: [O.DTI_NO_RATIO], fallback: '0' };
    }
    if (isBusinessPurpose(typeCode)) {
        return { visible: false, disabled: true, options: [O.DTI_NO_RATIO], fallback: '0' };
    }
    return {
        visible: true,
        disabled: false,
        options: pick(O.DTI, ['20', '44', '46', '55']),
        fallback: '20',
    };
}

/**
 * Prepayment penalty is offered on business-purpose investment loans only.
 * Two state overlays apply: some states forbid a no-penalty option, and MI/OH
 * cap the penalty at 1% for small unit counts.
 */
function pppRule(typeCode, occupancy, state, units, closingEntity) {
    if (!(isBusinessPurpose(typeCode) && occupancy === '2')) {
        return { visible: false, disabled: false, options: pick(O.PPP_TERM, ['0']), fallback: '0' };
    }

    let allowNoPenalty = NO_PPP_STATES.includes(state);
    if (!allowNoPenalty) allowNoPenalty = state === 'IL' && closingEntity === '3';

    const options = O.PPP_TERM.filter((o) => o.value !== '0' || allowNoPenalty);

    const onePercent = (state === 'MI' && units === '1') || (state === 'OH' && ['1', '2'].includes(units));
    const labelled = onePercent
        ? options.map((o) => ({ ...o, label: o.label.replace('5%', '1%') }))
        : options;

    return { visible: true, disabled: false, options: labelled, fallback: '5' };
}

/**
 * YSP appears only when the product can price above par, and only on Wholesale —
 * correspondent hides both YSP and broker compensation entirely.
 */
function yspRule(typeCode, occupancy, units, brokerCompensation, correspondent) {
    const eligible =
        !correspondent && occupancy === '2' && isBusinessPurpose(typeCode) && brokerCompensation !== '2';
    if (!eligible) return { visible: false, options: O.YSP_NA, fallback: '0.000' };

    // Accelerator on 5-8 units prices off its own rate-sheet row.
    const key = isAccelerator(typeCode) && ['5', '6', '7', '8'].includes(units) ? 758 : Number(typeCode);
    if ((O.MAX_PRICE_BY_TYPE[key] ?? 0) <= 100) {
        return { visible: false, options: O.YSP_NA, fallback: '0.000' };
    }

    const options = [];
    for (let p = 0; p < 3; p += 0.125) options.push({ value: p.toFixed(3), label: p.toFixed(3) });
    options.push({ value: '3.000', label: '3.000' });
    return { visible: true, options, fallback: '0.000' };
}

function texas50a6Rule(state, purpose, citizenship, occupancy, incomeLabel, loanAmount) {
    const forcedOn = state === 'TX' && occupancy === '0' && purpose === '2';
    const visible =
        state === 'TX' &&
        purpose !== '0' &&
        citizenship !== '3' &&
        occupancy === '0' &&
        !incomeLabel.includes('DSCR') &&
        !incomeLabel.includes('No Ratio') &&
        loanAmount <= SUPER_JUMBO_THRESHOLD;

    return { visible, forcedOn, disabled: !visible || forcedOn };
}

function brokerCompensationRule(typeCode, occupancy, state, purpose, isTexas50A6, correspondent) {
    // Correspondent pays no broker compensation, so the field is hidden and pinned.
    if (correspondent) {
        return { visible: false, disabled: true, forced: '1', options: O.BROKER_COMPENSATION };
    }
    if (isTexas50A6) {
        // A Texas 50(a)(6) loan must be lender-paid.
        const options = state === 'TX' && occupancy === '0'
            ? pick(O.BROKER_COMPENSATION, ['2'])
            : O.BROKER_COMPENSATION;
        return { visible: true, disabled: true, forced: '2', options };
    }
    if (isAlly(typeCode) || occupancy === '2') {
        return { visible: true, disabled: true, forced: '1', options: O.BROKER_COMPENSATION };
    }
    return { visible: true, disabled: false, forced: null, options: O.BROKER_COMPENSATION };
}

/* ---------------------------------------------------------------- scenario */

/**
 * Everything the UI needs for one scenario. Pure: same form in, same out.
 */
export function deriveScenario(form) {
    const correspondent = isCorrespondent(form.channel);
    const incomeLabel = incomeLabelFor(form.citizenship, form.incomeType);
    const typeCode = calculateLoanType({
        citizenship: form.citizenship,
        occupancy: form.occupancy,
        incomeType: form.incomeType,
        incomeLabel,
        loanAmount: form.loanAmount,
        correspondent,
    });

    const units = unitAvailability(typeCode, form.propertyType, incomeLabel);
    const dti = dtiRule(typeCode, form.occupancy, form.incomeType, incomeLabel);
    const ppp = pppRule(typeCode, form.occupancy, form.state, form.units, form.closingEntity);
    const texas = texas50a6Rule(
        form.state, form.purpose, form.citizenship, form.occupancy, incomeLabel, form.loanAmount,
    );
    const isTexas50A6 = texas.visible ? (texas.forcedOn || form.isTexas50A6) : false;
    const brokerComp = brokerCompensationRule(
        typeCode, form.occupancy, form.state, form.purpose, isTexas50A6, correspondent,
    );
    const brokerCompensation = brokerComp.forced ?? form.brokerCompensation;
    const ysp = yspRule(typeCode, form.occupancy, form.units, brokerCompensation, correspondent);

    // Occupancy is dictated by the income type for the DSCR entries.
    const occupancyLocked = OCCUPANCY_LOCKING_INCOME_TYPES.includes(form.incomeType);
    const isDscrSelected = DSCR_INCOME_TYPES.includes(form.incomeType);
    const dscrScenario = form.occupancy === '2' && isDscrSelected;

    const assetDepletionVisible = dscrScenario && ['700', '701'].includes(form.incomeType);
    const assetDepletion = assetDepletionVisible && form.assetDepletion;
    const preAssetDepletionVisible =
        assetDepletion &&
        ((isAccelerator(typeCode) && Number(form.units) < 5) || isAmbassador(typeCode));

    // Interest Only is an Accelerator / Ambassador feature only.
    const interestOnlyDisabled =
        isAmbassadorSecondHomeIncome(typeCode, form.occupancy, form.incomeType) ||
        isSuperJumbo(typeCode) || isItinAccelerator(typeCode) || isItinActivator(typeCode) ||
        isActivator(typeCode) || isAlly(typeCode);

    const escrowWaiverDisabled = isAlly(typeCode);
    const escrowWaiver = escrowWaiverDisabled ? false : form.escrowWaiver;

    const closingEntityVisible =
        isBusinessPurpose(typeCode) && form.occupancy === '2' && ['NJ', 'IL'].includes(form.state);

    const bankStatementMonthsVisible =
        ['0', '1'].includes(form.citizenship) &&
        ['0', '1'].includes(form.occupancy) &&
        form.purpose !== '3' &&
        ['15', '9015'].includes(form.incomeType);

    const creditSeasoningTight =
        isAlly(typeCode) || isItinActivator(typeCode) || isAmbassador(typeCode) ||
        (isAccelerator(typeCode) && ['5', '6', '7', '8'].includes(form.units));

    return {
        typeCode,
        correspondent,
        productName: productName(typeCode, { occupancy: form.occupancy, incomeType: form.incomeType }),
        productNamePrefix: productNamePrefix(typeCode, { occupancy: form.occupancy, incomeType: form.incomeType }),
        incomeLabel,

        options: {
            citizenship: O.CITIZENSHIP,
            incomeType: incomeTypeOptions(form.citizenship, correspondent),
            occupancy: O.OCCUPANCY,
            purpose: purposeOptions(typeCode),
            lockPeriod: correspondent ? O.LOCK_PERIOD_CORRESPONDENT : O.LOCK_PERIOD,
            amortizationType: amortizationOptions(typeCode),
            bankStatementMonths: O.BANK_STATEMENT_MONTHS,
            closingEntity: O.CLOSING_ENTITY,
            preAssetDepletion: O.PRE_ASSET_DEPLETION,
            propertyType: propertyTypeOptions(typeCode),
            units: O.UNITS.map((o) => ({ ...o, disabled: !units.allowed.includes(o.value) })),
            state: stateOptions(form.occupancy, correspondent),
            brokerCompensation: brokerComp.options,
            dti: dti.options,
            ysp: ysp.options,
            pppTerm: ppp.options,
            housingEvent: O.HOUSING_EVENT,
            creditSeasoning: O.CREDIT_SEASONING.map((o) =>
                o.value === '5' ? { ...o, label: creditSeasoningTight ? '0x30x12' : '0x30x24' } : o,
            ),
            lienPosition: O.LIEN_POSITION,
        },

        visible: {
            bankStatementMonths: bankStatementMonthsVisible,
            closingEntity: closingEntityVisible,
            preAssetDepletion: preAssetDepletionVisible,
            dti: dti.visible,
            ysp: ysp.visible,
            pppTerm: ppp.visible,
            brokerCompensation: brokerComp.visible,
            compPlan: brokerComp.visible && brokerCompensation === '2',
            // CDFI is a Wholesale-only concession, not available for SkyTPO.
            cdfi: !correspondent && getOrgConfig().id !== 'SkyTPO',
            // ACH is a Wholesale-only option, only for Investment occupancy, and
            // only for orgs that enable it.
            ach: !correspondent && form.occupancy === '2' && getOrgConfig().showAchCheckbox === true,
            shortTermRental:
                form.occupancy === '2' && form.propertyType !== '4D' && isBusinessPurpose(typeCode),
            assetDepletion: assetDepletionVisible,
            isTexas50A6: texas.visible,
            // Buyout applies to Wholesale owner-occupied loans with documented income.
            lenderFeeBuyout: !correspondent && form.occupancy !== '2' && form.incomeType !== '21',
            // Correspondent warns that escrows may be required.
            escrowWaiverNote: correspondent,
            // Lien Position is only available for SkyTPO.
            lienPosition: getOrgConfig().id === 'SkyTPO',
        },

        disabled: {
            occupancy: occupancyLocked,
            amortizationType: isSuperJumbo(typeCode),
            dti: dti.disabled,
            brokerCompensation: brokerComp.disabled,
            interestOnly: interestOnlyDisabled,
            escrowWaiver: escrowWaiverDisabled,
            isTexas50A6: texas.disabled,
            fico: form.noFico,
        },

        // Values the rules pin regardless of what the user last chose.
        forced: {
            brokerCompensation: brokerComp.forced,
            isTexas50A6: texas.visible ? (texas.forcedOn ? true : null) : false,
            escrowWaiver: escrowWaiverDisabled ? false : null,
            interestOnly: interestOnlyDisabled ? false : null,
        },

        // Hidden fields the pricing engine expects, derived from the visible ones.
        hidden: hiddenFields(form, {
            typeCode, incomeLabel, isTexas50A6, escrowWaiver, assetDepletion,
            pppVisible: ppp.visible,
        }),

        limits: {
            dtiFallback: dti.fallback,
            pppFallback: ppp.fallback,
            yspFallback: ysp.fallback,
            unitFallback: units.fallback,
            unitAllowed: units.allowed,
            ficoFallback: 660,
            ltvFallback: 75,
            loanAmountMaxFallback: 300_000_0,
        },
    };
}

/**
 * The original form carries a second, hidden copy of several answers in the
 * shape the pricing engine wants (ChpDocType, PropertyType, Term, ...). These
 * are the mappings from Change_IncomeType / Change_Units / Change_AmortizationType
 * and Set_Others.
 */
function hiddenFields(form, ctx) {
    const { typeCode, incomeLabel } = ctx;
    const amort = O.AMORTIZATION_MAP[form.amortizationType] ?? O.AMORTIZATION_MAP[2];

    // ChpDocType + the two DSCR selectors, per Change_IncomeType.
    let chpDocType = form.incomeType;
    let dscrForAccelerator = '';
    let dscrForItinAccelerator = '';

    if (form.incomeType === '21' && form.occupancy === '2') {
        chpDocType = '21';
        dscrForAccelerator = '3';
    } else if (isAmbassador(typeCode)) {
        const level = form.incomeType.replace('70', '');
        dscrForAccelerator = level;
        chpDocType = level === '3' ? '21' : '20';
    } else if (form.citizenship === '4') {
        if (form.incomeType.startsWith('80')) {
            dscrForItinAccelerator = form.incomeType.replace('80', '');
            chpDocType = '20';
        }
    } else if (form.incomeType.startsWith('70')) {
        dscrForAccelerator = form.incomeType.replace('70', '');
        chpDocType = '20';
    } else if (form.incomeType.startsWith('90')) {
        chpDocType = form.incomeType.replace('90', '');
        dscrForAccelerator = chpDocType;
    }

    // Multi-Unit resolves to a "real" property type via the unit count.
    const realPropertyType =
        form.propertyType === 'MU' ? (O.UNITS_TO_PROPERTY_TYPE[form.units] ?? '8') : form.propertyType;

    let tradelines = '6';
    if (isAlly(typeCode)) tradelines = '2';
    else if (isItinAccelerator(typeCode)) tradelines = '4';

    const dtiForActivator =
        isActivator(typeCode) || isItinActivator(typeCode) ? form.dti : '';

    return {
        chpDocType,
        dscrForAccelerator,
        dscrForItinAccelerator,
        realPropertyType,
        // Condo variants are attached; everything else detached.
        attachmentType: ['2', '4S', '4D'].includes(realPropertyType) ? 'Attached' : 'Detached',
        tradelines,
        term: amort.term,
        armFixedTerms: amort.fixedTerm,
        amortizationTypeInHero: amort.amortInHero,
        realDti: form.dti === '0' ? '' : form.dti,
        dtiForActivator,
        // Only a product that actually offers a prepayment penalty sends a term.
        // Credit-qualifying products must send an empty string — sending '0' makes
        // the engine return no programs at all. Ambassador outside Investment is the
        // one exception, and sends '0' (see getUpdatedData in easy.QuickPrice.js).
        realPppTerm: ctx.pppVisible
            ? form.pppTerm
            : isAmbassador(typeCode) && form.occupancy !== '2'
                ? '0'
                : '',
        impound: !ctx.escrowWaiver,
        closeInNameOfAnEntity: form.occupancy === '2',
        foreignNational: form.citizenship === '3',
        incomeLabel,
        citizenshipLabel: O.CITIZENSHIP[form.citizenship].label
    };
}

/* --------------------------------------------------------------- normalize */

/**
 * After an edit, snap the scenario back onto legal values — the job the
 * `Set_` / `Original_` function pairs do in the original. Runs to a fixed point
 * because the product can change as a result of a snap, which in turn changes
 * which options are legal.
 */
export function normalize(form) {
    let current = form;
    for (let pass = 0; pass < 5; pass += 1) {
        const next = normalizeOnce(current);
        if (next === current) break;
        current = next;
    }
    return current;
}

function normalizeOnce(form) {
    const s = deriveScenario(form);
    const patch = {};

    const snap = (key, options, fallback) => {
        const legal = options.filter((o) => !o.disabled).map((o) => o.value);
        if (!legal.includes(form[key])) patch[key] = legal.includes(fallback) ? fallback : legal[0];
    };

    snap(
        'incomeType',
        s.options.incomeType,
        defaultIncomeType(form.citizenship, isCorrespondent(form.channel)),
    );
    snap('purpose', s.options.purpose, '2');
    snap('amortizationType', s.options.amortizationType, '2');
    snap('propertyType', s.options.propertyType, '0');
    snap('units', s.options.units, s.limits.unitFallback);
    snap('state', s.options.state, 'AZ');
    snap('dti', s.options.dti, s.limits.dtiFallback);
    snap('pppTerm', s.options.pppTerm, s.limits.pppFallback);
    snap('ysp', s.options.ysp, s.limits.yspFallback);
    snap('brokerCompensation', s.options.brokerCompensation, '1');
    snap('lockPeriod', s.options.lockPeriod, '30');

    // Values the rules pin outright.
    for (const [key, value] of Object.entries(s.forced)) {
        if (value !== null && form[key] !== value) patch[key] = value;
    }

    // Checkboxes and inputs that a hidden field must not keep stale state for.
    if (!s.visible.assetDepletion && form.assetDepletion) patch.assetDepletion = false;
    if (!s.visible.shortTermRental && form.shortTermRental) patch.shortTermRental = false;
    if (!s.visible.ach && form.ach) patch.ach = false;
    if (!s.visible.lenderFeeBuyout && form.lenderFeeBuyout) patch.lenderFeeBuyout = false;
    if (!s.visible.bankStatementMonths && form.bankStatementMonths !== '12') {
        patch.bankStatementMonths = '12';
    }
    if (!s.visible.compPlan && form.compPlanRate !== '1') patch.compPlanRate = '1';
    if (!s.visible.lienPosition && form.lienPosition !== '1') patch.lienPosition = '1';

    return Object.keys(patch).length ? { ...form, ...patch } : form;
}

/* -------------------------------------------------------------- transitions */

/**
 * Cross-field effects that depend on *what changed*, not just on the resulting
 * scenario — the bodies of Change_Citizenship, Change_IncomeType's
 * autoChangeOccupancy, and Change_Occupancy.
 */
const TRANSITIONS = {
    // Switching channel is the equivalent of navigating to the other pricer, so
    // the scenario starts over from that channel's defaults.
    channel(form) {
        return { ...INITIAL_FORM, channel: form.channel };
    },

    citizenship(form, previous) {
        if (form.citizenship === '3') {
            // Foreign National: DSCR scenarios are investment, everything else second home.
            return { occupancy: DSCR_INCOME_TYPES.includes(previous.incomeType) ? '2' : '1' };
        }
        if (form.citizenship === '4') return { occupancy: '0' };
        return null;
    },

    incomeType(form) {
        const label = incomeLabelFor(form.citizenship, form.incomeType);
        if (label.includes('DSCR') || (label.includes('No Ratio') && ['3', '4'].includes(form.citizenship))) {
            return { occupancy: '2' };
        }
        if (form.citizenship === '3' && !DSCR_INCOME_TYPES.includes(form.incomeType)) {
            return { occupancy: '1' };
        }
        return { occupancy: '0' };
    },

    occupancy(form) {
        // Leaving Investment with a DSCR income type falls back to full doc.
        const label = incomeLabelFor(form.citizenship, form.incomeType);
        if (form.occupancy !== '2' && form.citizenship !== '3' && label.includes('DSCR')) {
            return { incomeType: form.citizenship === '4' ? '0' : '900' };
        }
        return null;
    },
};

/**
 * Apply one user edit: set the field, run any transition it triggers, then
 * normalize the result back onto legal values.
 */
export function applyEdit(form, field, value) {
    const edited = { ...form, [field]: value };
    const patch = TRANSITIONS[field]?.(edited, form);
    const next = normalize(patch ? { ...edited, ...patch } : edited);

    // ACH starts checked whenever the field newly appears.
    if (!deriveScenario(form).visible.ach && deriveScenario(next).visible.ach) {
        return { ...next, ach: true };
    }
    return next;
}

/**
 * Property value / purchase price is always loan amount grossed up by LTV.
 * Truncated, not rounded — toMoney(value, 0) in the original drops the decimals.
 */
export const impliedPropertyValue = (loanAmount, ltv) =>
    ltv > 0 ? Math.floor((loanAmount / ltv) * 100) : 0;
